import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  MessageSquare,
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle2,
  Home,
  FileText,
  DollarSign,
  Calendar,
  Building2,
  User,
  CreditCard,
  Receipt,
  Phone,
  Mail,
  MapPin,
  Send,
  Copy,
  X,
  Banknote,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * Resident/Client Portal Dashboard
 * Supports both Renters (tenants) and Condo Owners
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function ResidentDashboard() {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      const response = await axios.get(`${API}/payments/my-payment-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentInfo(response.data);
      if (response.data.monthly_amount) {
        setPaymentAmount(response.data.monthly_amount.toString());
      }
    } catch (error) {
      console.error('Failed to fetch payment info:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [dashboardRes, communicationsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/communications`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setDashboardData(dashboardRes.data);
      setRecentMessages(communicationsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  const handleSubmitPaymentNotification = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    setSubmittingPayment(true);
    try {
      await axios.post(
        `${API}/payments/request`,
        {
          amount: parseFloat(paymentAmount),
          payment_type: paymentInfo?.payment_type || 'rent',
          description: paymentDescription || `e-Transfer payment for ${paymentInfo?.payment_type || 'rent'}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert('Payment notification sent! Management will confirm once received.');
      setShowPaymentModal(false);
      setPaymentDescription('');
      fetchPaymentInfo(); // Refresh to show pending request
    } catch (error) {
      console.error('Failed to submit payment notification:', error);
      alert('Failed to submit payment notification. Please try again.');
    } finally {
      setSubmittingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const clientType = dashboardData?.client_type || 'unknown';
  const isRenter = clientType === 'renter';
  const isOwner = clientType === 'owner';
  const lease = dashboardData?.my_lease;
  const unit = dashboardData?.my_unit;
  const property = dashboardData?.my_property;
  const payments = dashboardData?.my_payments || [];
  const maintenanceRequests = dashboardData?.my_maintenance_requests || [];
  const openRequests = dashboardData?.open_requests || 0;

  return (
    <div className="space-y-6" data-testid="resident-dashboard">
      {/* Welcome Header */}
      <div className={`rounded-xl p-6 text-white ${isRenter ? 'bg-gradient-to-r from-indigo-600 to-violet-600' : 'bg-gradient-to-r from-emerald-600 to-teal-600'}`}>
        <div className="flex items-center gap-3 mb-2">
          {isRenter ? <FileText className="w-6 h-6" /> : <Home className="w-6 h-6" />}
          <span className="text-sm font-medium px-2 py-1 bg-white/20 rounded-full">
            {isRenter ? 'Tenant Portal' : isOwner ? 'Owner Portal' : 'Client Portal'}
          </span>
        </div>
        <h1 className="text-2xl font-bold">Welcome, {user?.first_name || 'Resident'}!</h1>
        <p className="text-white/80 mt-1">
          {isRenter 
            ? 'Manage your lease, payments, and maintenance requests.' 
            : isOwner 
              ? 'View your unit, maintenance fees, and submit requests.'
              : 'Manage your property needs and communicate with management.'}
        </p>
      </div>

      {/* Property Info Card */}
      {property && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-slate-100 p-2 rounded-lg">
              <Building2 className="w-5 h-5 text-slate-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">My Property</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-500">Property Name</p>
              <p className="font-medium text-slate-900">{property.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Address</p>
              <p className="font-medium text-slate-900">{property.address}, {property.city}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Property Type</p>
              <p className="font-medium text-slate-900 capitalize">{property.type}</p>
            </div>
          </div>
        </div>
      )}

      {/* Lease Info (for Renters) */}
      {isRenter && lease && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">My Lease</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              lease.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
            }`}>
              {lease.status}
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Unit</p>
              <p className="font-semibold text-slate-900">{lease.unit_number}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Monthly Rent</p>
              <p className="font-semibold text-indigo-600">{formatCurrency(lease.monthly_rent)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Lease Start</p>
              <p className="font-semibold text-slate-900">{formatDate(lease.start_date)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Lease End</p>
              <p className="font-semibold text-slate-900">{formatDate(lease.end_date)}</p>
            </div>
          </div>

          {/* Additional Fees */}
          {(lease.parking_fee > 0 || lease.storage_fee > 0) && (
            <div className="border-t border-slate-100 pt-4 mt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Additional Fees</p>
              <div className="flex gap-4">
                {lease.parking_fee > 0 && (
                  <span className="text-sm text-slate-600">Parking: {formatCurrency(lease.parking_fee)}/mo</span>
                )}
                {lease.storage_fee > 0 && (
                  <span className="text-sm text-slate-600">Storage: {formatCurrency(lease.storage_fee)}/mo</span>
                )}
              </div>
              <p className="text-sm font-semibold text-indigo-600 mt-2">
                Total Monthly: {formatCurrency(lease.total_monthly)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Unit Info (for Condo Owners) */}
      {isOwner && unit && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <Home className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">My Unit</h3>
            </div>
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
              Owner
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Unit Number</p>
              <p className="font-semibold text-slate-900">{unit.unit_number}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Monthly Maintenance Fee</p>
              <p className="font-semibold text-emerald-600">{formatCurrency(unit.monthly_maintenance_fee || unit.monthly_fee || unit.monthly_rent)}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Floor</p>
              <p className="font-semibold text-slate-900">{unit.floor || 'N/A'}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Size</p>
              <p className="font-semibold text-slate-900">{unit.square_feet ? `${unit.square_feet} sq ft` : 'N/A'}</p>
            </div>
          </div>

          {/* Unit Details */}
          {(unit.bedrooms || unit.bathrooms) && (
            <div className="border-t border-slate-100 pt-4 mt-4">
              <div className="flex gap-4 text-sm text-slate-600">
                {unit.bedrooms && <span>{unit.bedrooms} Bedroom{unit.bedrooms > 1 ? 's' : ''}</span>}
                {unit.bathrooms && <span>{unit.bathrooms} Bathroom{unit.bathrooms > 1 ? 's' : ''}</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Pay Rent / Maintenance Fee Button */}
        {paymentInfo?.has_payment_info && paymentInfo?.etransfer_enabled && (
          <button
            onClick={() => setShowPaymentModal(true)}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl p-5 hover:shadow-lg hover:from-emerald-600 hover:to-teal-600 transition-all group text-left"
            data-testid="pay-rent-btn"
          >
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-xl group-hover:bg-white/30 transition-colors">
                <Banknote className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">Pay {isRenter ? 'Rent' : 'Maintenance Fee'}</h3>
                <p className="text-sm text-white/80">via Interac e-Transfer</p>
              </div>
            </div>
          </button>
        )}

        <Link
          to="/maintenance"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-300 transition-all group"
          data-testid="submit-maintenance-btn"
        >
          <div className="flex items-center gap-4">
            <div className="bg-indigo-100 p-3 rounded-xl group-hover:bg-indigo-200 transition-colors">
              <Wrench className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Submit Maintenance Request</h3>
              <p className="text-sm text-slate-500">Report an issue with your unit</p>
            </div>
          </div>
        </Link>

        <Link
          to="/communications"
          className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-indigo-300 transition-all group"
          data-testid="message-center-btn"
        >
          <div className="flex items-center gap-4">
            <div className="bg-violet-100 p-3 rounded-xl group-hover:bg-violet-200 transition-colors">
              <MessageSquare className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Message Center</h3>
              <p className="text-sm text-slate-500">Contact property management</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-rose-100 p-2 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-rose-600">{openRequests}</p>
              <p className="text-sm text-slate-500">Open Requests</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">
                {maintenanceRequests.filter(r => r.status === 'in_progress').length}
              </p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">
                {maintenanceRequests.filter(r => r.status === 'completed' || r.status === 'resolved').length}
              </p>
              <p className="text-sm text-slate-500">Completed</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isRenter ? 'bg-indigo-100' : 'bg-emerald-100'}`}>
              <Receipt className={`w-5 h-5 ${isRenter ? 'text-indigo-600' : 'text-emerald-600'}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${isRenter ? 'text-indigo-600' : 'text-emerald-600'}`}>
                {payments.length}
              </p>
              <p className="text-sm text-slate-500">Payments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isRenter ? 'bg-indigo-100' : 'bg-emerald-100'}`}>
                <DollarSign className={`w-5 h-5 ${isRenter ? 'text-indigo-600' : 'text-emerald-600'}`} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                {isRenter ? 'Rent Payment History' : 'Maintenance Fee History'}
              </h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Method</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Reference</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.slice(0, 5).map((payment) => (
                  <tr key={payment.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 px-3 text-sm text-slate-900">{formatDate(payment.payment_date)}</td>
                    <td className={`py-3 px-3 text-sm font-medium ${isRenter ? 'text-indigo-600' : 'text-emerald-600'}`}>
                      {formatCurrency(payment.amount)}
                    </td>
                    <td className="py-3 px-3 text-sm text-slate-600 capitalize">{payment.payment_method}</td>
                    <td className="py-3 px-3 text-sm text-slate-500">{payment.reference_number || '-'}</td>
                    <td className="py-3 px-3">
                      <span className="px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700">
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Activity - Two Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Maintenance Requests */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">My Maintenance Requests</h3>
            <Link to="/maintenance" className="text-sm text-indigo-600 hover:text-indigo-700">
              View All →
            </Link>
          </div>
          <div className="space-y-3">
            {maintenanceRequests.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <CheckCircle2 className="w-5 h-5 mr-2 text-emerald-500" />
                No maintenance requests
              </div>
            ) : (
              maintenanceRequests.map((req) => (
                <div key={req.id} className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      req.priority === 'high' || req.priority === 'urgent' ? 'bg-rose-100' : 
                      req.priority === 'medium' ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <Wrench className={`w-4 h-4 ${
                        req.priority === 'high' || req.priority === 'urgent' ? 'text-rose-600' : 
                        req.priority === 'medium' ? 'text-amber-600' : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{req.title}</p>
                      <p className="text-xs text-slate-500">{req.category}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    req.status === 'open' ? 'bg-rose-100 text-rose-700' : 
                    req.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {req.status.replace('_', ' ')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Messages */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">Recent Messages</h3>
            <Link to="/communications" className="text-sm text-indigo-600 hover:text-indigo-700">
              View All →
            </Link>
          </div>
          <div className="space-y-3">
            {recentMessages.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-slate-500">
                <MessageSquare className="w-5 h-5 mr-2 text-slate-300" />
                No messages yet
              </div>
            ) : (
              recentMessages.map((msg) => (
                <div key={msg.id} className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
                  <div className="flex items-start gap-3">
                    <div className="bg-indigo-100 p-2 rounded-lg">
                      <MessageSquare className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{msg.subject}</p>
                      <p className="text-xs text-slate-500">{msg.category}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    msg.status === 'open' ? 'bg-rose-100 text-rose-700' : 
                    msg.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                    'bg-emerald-100 text-emerald-700'
                  }`}>
                    {msg.status.replace('_', ' ')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-2">Need Help?</h3>
        <p className="text-sm text-slate-600">
          For emergency maintenance issues outside of business hours, please contact the emergency line at <strong>1-800-ALTAM-911</strong>.
          For general inquiries, use the Message Center to reach our property management team.
        </p>
      </div>

      {/* E-Transfer Payment Modal */}
      {showPaymentModal && paymentInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Banknote className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Pay via Interac e-Transfer</h2>
                    <p className="text-white/80 text-sm">{paymentInfo.property_name}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowPaymentModal(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Payment Details */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-slate-500 mb-3">Payment Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Unit</span>
                    <span className="font-medium text-slate-900">{paymentInfo.unit_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Payment Type</span>
                    <span className="font-medium text-slate-900 capitalize">
                      {paymentInfo.payment_type?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Monthly Amount</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(paymentInfo.monthly_amount)}</span>
                  </div>
                </div>
              </div>

              {/* E-Transfer Instructions */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">How to Pay</h3>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-emerald-100 text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="text-slate-900 font-medium">Open your bank's e-Transfer feature</p>
                      <p className="text-sm text-slate-500">Log in to your online banking or mobile app</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-emerald-100 text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="text-slate-900 font-medium">Send to:</p>
                      <div className="mt-2 bg-slate-100 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Recipient Email</p>
                          <p className="font-mono font-medium text-slate-900">{paymentInfo.etransfer_email}</p>
                        </div>
                        <button
                          onClick={() => copyToClipboard(paymentInfo.etransfer_email)}
                          className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
                          title="Copy email"
                        >
                          {copiedEmail ? (
                            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          ) : (
                            <Copy className="w-5 h-5 text-slate-500" />
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        Recipient: <span className="font-medium">{paymentInfo.etransfer_recipient_name}</span>
                      </p>
                      {paymentInfo.autodeposit_enabled && (
                        <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Auto-deposit enabled (no security question needed)
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-emerald-100 text-emerald-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="text-slate-900 font-medium">Include in message:</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {paymentInfo.etransfer_instructions || `Your name, unit number (${paymentInfo.unit_number}), and payment month`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Amount Input */}
              <div className="border-t border-slate-100 pt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Amount you're sending
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
              </div>

              {/* Note (optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Note (optional)
                </label>
                <textarea
                  value={paymentDescription}
                  onChange={(e) => setPaymentDescription(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  rows={2}
                  placeholder="e.g., February 2026 rent payment"
                />
              </div>

              {/* Pending Payment Requests */}
              {paymentInfo.pending_requests?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700 mb-2">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium text-sm">Pending Confirmation</span>
                  </div>
                  <p className="text-sm text-amber-600">
                    You have {paymentInfo.pending_requests.length} payment(s) awaiting confirmation from management.
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitPaymentNotification}
                  disabled={submittingPayment || !paymentAmount}
                  className="flex-1 px-4 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submittingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      I've Sent the e-Transfer
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Click "I've Sent the e-Transfer" after you've completed the transfer through your bank. 
                Management will confirm receipt within 1-2 business days.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
